import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import Seo from '../components/Seo';
import SectionContact from '../components/SectionContact';
import Bande from '../components/Bande';
import Reveal from '../components/Reveal';
import TitreSection from '../components/TitreSection';
import CarteMachine from '../components/CarteMachine';
import AppelAction from '../components/AppelAction';
import Faq from '../components/Faq';
import Avis from '../components/Avis';
import { COMMUNES_PAGES, MACHINES, ENTREPRISE, FAQ } from '../data/site';

export default function PageCommune({ commune }) {
  const c = commune;
  if (!c) return <Navigate to="/livraison-ile-de-france" replace />;

  const autres = COMMUNES_PAGES.filter((x) => x.slug !== c.slug);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: "Location d'engins de chantier",
    provider: {
      '@type': 'LocalBusiness',
      name: ENTREPRISE.nom,
      telephone: '+33601244724',
      address: {
        '@type': 'PostalAddress',
        streetAddress: ENTREPRISE.adresse,
        postalCode: ENTREPRISE.codePostal,
        addressLocality: ENTREPRISE.ville,
        addressCountry: 'FR',
      },
    },
    areaServed: { '@type': 'City', name: c.nom, postalCode: c.cp },
  };

  return (
    <>
      <Seo
        titre={`Location d'engins de chantier à ${c.nom} (${c.cp})`}
        description={`Mini-pelles et chariot télescopique en location à ${c.nom}, avec ou sans chauffeur. Livraison sur chantier sous 24 à 48 h depuis Alfortville. Devis en quelques heures.`}
        jsonLd={jsonLd}
      />

      <section className="bg-nuit text-white relative overflow-hidden">
        <div className="absolute inset-0 trame-diagonale" aria-hidden="true" />
        <div className="conteneur relative py-14 sm:py-16">
          <nav className="font-cond uppercase tracking-[0.09em] text-[13px] text-white/50 mb-6" aria-label="Fil d'ariane">
            <Link to="/" className="hover:text-jaune">Accueil</Link>
            <span className="mx-2">/</span>
            <Link to="/livraison-ile-de-france" className="hover:text-jaune">Zone d'intervention</Link>
            <span className="mx-2">/</span>
            <span className="text-white/80">{c.nom}</span>
          </nav>
          <h1 className="font-display uppercase text-[34px] sm:text-[46px] leading-[1] max-w-3xl">
            Location d'engins de chantier à <span className="mot-degrade">{c.nom}</span>
          </h1>
          <p className="mt-5 text-white/75 max-w-2xl text-[18px]">{c.intro}</p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <Link to="/contact" className="btn-jaune">Demander mon devis</Link>
            <a href={ENTREPRISE.telephoneLien} className="btn-contour">{ENTREPRISE.telephone}</a>
          </div>
        </div>
        <Bande hauteur={6} />
      </section>

      <section className="bg-white py-14 sm:py-16">
        <div className="conteneur grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-14">
          <div>
            <TitreSection avant="Ce qu'on apporte de " mot="concret" />
            <p className="mt-5 text-nuit/85 text-[17px]">{c.argument}</p>
            <ul className="mt-6 space-y-3">
              {[
                `Livraison sur votre chantier de ${c.nom} sous 24 à 48 h`,
                'Machines révisées et assurées, avec leurs documents de conformité',
                'Location à la journée, à la semaine ou au mois',
                'Avec ou sans chauffeur, selon vos équipes',
                "Intervention dans la journée en cas de souci sur la machine",
              ].map((x) => (
                <li key={x} className="flex gap-3">
                  <span className="mt-2 shrink-0 w-2.5 h-2.5 bg-jaune" aria-hidden="true" />
                  <span className="text-nuit/85">{x}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative self-start">
            <div className="absolute -inset-x-3 -bottom-2 h-2 bande-chantier" aria-hidden="true" />
            <img
              src="/images/ecr50d-remorque.jpg"
              alt={`Mini-pelle prête à partir en livraison vers un chantier de ${c.nom}`}
              loading="lazy"
              className="photo-entiere relative rounded-[4px] shadow-haut max-h-[400px] w-auto mx-auto"
            />
          </div>
        </div>
      </section>

      <AppelAction
        titre={`Un chantier à ${c.nom} ?`}
        texte="Dites-nous les dates, le type de travaux et la largeur de votre accès. On vous répond en quelques heures."
      />

      <section className="bg-trame py-14 sm:py-16">
        <div className="conteneur">
          <TitreSection avant="Les machines qu'on peut vous " mot="livrer" />
          <div className="mt-9 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {MACHINES.map((m) => (
              <Reveal key={m.slug}><CarteMachine machine={m} /></Reveal>
            ))}
          </div>
        </div>
      </section>

      <Avis />

      <Faq />

      <section className="bg-white py-12">
        <div className="conteneur">
          <h2 className="font-cond uppercase font-bold tracking-[0.06em] text-[18px] text-nuit">
            Nous intervenons aussi dans ces communes
          </h2>
          <ul className="mt-4 flex flex-wrap gap-2.5">
            {autres.map((a) => (
              <li key={a.slug}>
                <Link
                  to={`/location-engins-chantier-${a.slug}`}
                  className="inline-block font-cond uppercase tracking-[0.06em] text-[14px] bg-trame text-nuit px-3.5 py-2 border-l-2 border-bleu hover:border-jaune transition-colors"
                >
                  {a.nom}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <SectionContact
        avant="Votre chantier est à "
        mot={c.nom}
        apres=" ?"
        intro="Donnez-nous les dates, le type de travaux et la largeur de votre accès. On vous répond en quelques heures avec la machine qui convient et son tarif."
      />
    </>
  );
}
